import {useEffect, useRef, useState} from 'react';
import {COOKIES_CONSENT_GROUPS, useCookiesConsent} from './useCookiesConsent';

const KLAVIYO_LOAD_TIMEOUT = 15000; // 15 seconds
const KLAVIYO_CHECK_INTERVAL = 500; // Check every 500ms

/**
 * Custom hook to monitor Klaviyo script loading status with consent checking
 *
 * @returns {Object} Hook state object
 * @returns {boolean} returns.isKlaviyoLoaded - Whether Klaviyo is loaded and ready
 * @returns {boolean} returns.isLoadingKlaviyo - Whether currently checking for Klaviyo
 * @returns {boolean} returns.klaviyoLoadFailed - Whether Klaviyo failed to load or consent was denied
 */
export default function useKlaviyoLoadedInfo() {
  const {isCheckingConsent, isConsentGiven} = useCookiesConsent({
    targetingGroup: COOKIES_CONSENT_GROUPS.TARGETING
  });

  const [klaviyoLoaded, setKlaviyoLoaded] = useState(false);
  const [isLoadingKlaviyo, setIsLoadingKlaviyo] = useState(true);
  const klaviyoCheckTimeRef = useRef(null);
  const klaviyoObserverRef = useRef(null);

  useEffect(() => {
    const cleanup = () => {
      if (klaviyoCheckTimeRef.current) {
        clearTimeout(klaviyoCheckTimeRef.current);
        klaviyoCheckTimeRef.current = null;
      }
      if (klaviyoObserverRef.current) {
        clearInterval(klaviyoObserverRef.current);
        klaviyoObserverRef.current = null;
      }
    };

    const setFinalState = (loaded) => {
      setKlaviyoLoaded(loaded);
      setIsLoadingKlaviyo(false);
      cleanup();
    };

    if (isCheckingConsent) {
      return cleanup;
    }

    if (!isConsentGiven) {
      setFinalState(false);
      return cleanup;
    }

    if (isConsentGiven) {
      const checkKlaviyo = () => {
        try {
          // eslint-disable-next-line no-underscore-dangle
          if (window._klOnsite) {
            setFinalState(true);
            return true;
          }
        } catch (error) {
          setFinalState(false);
          return true;
        }
        return false;
      };

      if (checkKlaviyo()) {
        return cleanup;
      }

      klaviyoObserverRef.current = setInterval(
        checkKlaviyo,
        KLAVIYO_CHECK_INTERVAL
      );

      klaviyoCheckTimeRef.current = setTimeout(() => {
        setFinalState(false);
      }, KLAVIYO_LOAD_TIMEOUT);
    }

    return cleanup;
  }, [isConsentGiven, isCheckingConsent]);

  return {
    isKlaviyoLoaded: klaviyoLoaded,
    isLoadingKlaviyo,
    klaviyoLoadFailed: !klaviyoLoaded && !isLoadingKlaviyo
  };
}
